import { createRoute, defer } from '@tanstack/react-router'

import { DocumentsSkeleton } from '@/features/bookings/features/overview/routes/BookingDocumentsSkeleton'
import { entitySubscriptionsQueryOptions } from '@/features/subscriptions'
import { rootRoute } from '@/routes/root'
import { ensureAuthenticatedUser } from '@/utils/auth-wall'

import { bookingQueryOptions } from '../../../api/booking'
import { BookingErrorComponent } from '../components/BookingLayout'
import { BookingSkeleton } from '../components/BookingSkeleton'
import { BookingContainersSkeleton } from '../components/containers/BookingContainersSkeleton'
import { BookingCustomsSkeleton } from '../components/customs/BookingCustomsSkeleton'
import { BookingDetailsSkeleton } from '../components/details/BookingDetailsSkeleton'
import { BookingPriceBreakdownSkeleton } from '../components/pricing/BookingPriceBreakdownSkeleton'

export const bookingRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$id',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: BookingErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const subscriptions = context.queryClient?.ensureQueryData(entitySubscriptionsQueryOptions(id))
    // to show the main spinner only for the main booking data, remove the defer
    const booking = context?.queryClient?.ensureQueryData(bookingQueryOptions(id))
    return { booking: defer(booking), subscriptions: defer(subscriptions) }
  },
  pendingComponent: BookingSkeleton,
}).lazy(() => import('./BookingRoot').then((d) => d.Route))

export const bookingDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$id/details',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: BookingErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const booking = context.queryClient?.ensureQueryData(bookingQueryOptions(id))
    return { booking: defer(booking) }
  },
  pendingComponent: BookingDetailsSkeleton,
}).lazy(() => import('./BookingDetails').then((d) => d.Route))

export const bookingContainersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$id/containers',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: BookingErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const booking = context.queryClient?.ensureQueryData(bookingQueryOptions(id))
    return { booking: defer(booking) }
  },
  pendingComponent: BookingContainersSkeleton,
}).lazy(() => import('./BookingContainers').then((d) => d.Route))

export const bookingPriceBreakdownRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$id/price-breakdown',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: BookingErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const booking = context.queryClient?.ensureQueryData(bookingQueryOptions(id))
    return { booking: defer(booking) }
  },
  pendingComponent: BookingPriceBreakdownSkeleton,
}).lazy(() => import('./BookingPriceBreakdown').then((d) => d.Route))

export const bookingCustomsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$id/customs',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: BookingErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const booking = context.queryClient?.ensureQueryData(bookingQueryOptions(id))
    return { booking: defer(booking) }
  },
  pendingComponent: BookingCustomsSkeleton,
}).lazy(() => import('./BookingCustoms').then((d) => d.Route))

export const bookingDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$id/documents',
  beforeLoad: ensureAuthenticatedUser,
  errorComponent: BookingErrorComponent,
  loader: async ({ context, params: { id } }) => {
    if (!context.queryClient) return
    const booking = context.queryClient?.ensureQueryData(bookingQueryOptions(id))
    return { booking: defer(booking) }
  },
  pendingComponent: DocumentsSkeleton,
}).lazy(() => import('./BookingDocuments').then((d) => d.Route))
